import React, { useState, useEffect } from 'react';
import { Autocomplete } from '@material-ui/lab';
import { FieldRenderProps } from 'react-final-form';

import { FieldInputValue } from 'components/Forms';
import { makeStyles } from '@material-ui/core';
import { feedbackButtonZIndex } from 'styles/defaultTheme';
import Input from './Input';
import { InputFieldProps } from './utils';
import InputFieldWrapper from './InputFieldWrapper';

const useStyles = makeStyles(
  theme => ({
    root: {
      display: 'flex',
      alignItems: 'baseline',
      justifyContent: 'space-between',
      width: '100%'
    },
    popper: {
      // The previous import of `feedbackButtonZIndex` was breaking modules for tests. Probably a circular dependency
      zIndex: feedbackButtonZIndex
    },
    chipDeleteIcon: {
      fill: theme.palette.aegis.semantic.message.dark
    }
  }),
  { name: 'FormMultiselectField' }
);

const FormMultiselectField = ({
  field,
  input,
  meta,
  optionRenderer: OptionRenderer
}: {
  field: InputFieldProps;
  optionRenderer: (option: FieldInputValue) => JSX.Element;
} & FieldRenderProps<FieldInputValue[], HTMLElement>) => {
  const classes = useStyles();

  // Local state for the selected options shown in the Autocomplete. We own
  // this state once mounted; it's seeded from `input.value` and is otherwise
  // only mutated through the `onChange` handler below or by the conflict-
  // status resync effect.
  const [inputValue, setInputValue] = useState<FieldInputValue[]>(() => {
    const initial = Array.isArray(input.value) ? input.value : [];
    return initial.map(val => {
      const latestOption = field.options?.find(
        opt => opt.value === (typeof val === 'string' ? val : val.value)
      );
      return (
        latestOption ||
        (typeof val === 'string' ? { value: val, label: val } : val)
      );
    });
  });

  // One-shot sync from form state → local state. If the Form mounted before
  // its initialValues were ready (e.g. async data still loading) and later
  // re-initialises with values, this picks them up. Crucially, it never
  // overwrites a populated local state — once we have chips, transient
  // empty `input.value` (e.g. from a brief reinit while a parallel fetch is
  // in flight) cannot wipe them.
  useEffect(() => {
    if (
      Array.isArray(input.value) &&
      input.value.length > 0 &&
      inputValue.length === 0
    ) {
      const resolved = input.value.map(val => {
        const latestOption = field.options?.find(
          opt => opt.value === (typeof val === 'string' ? val : val.value)
        );
        return (
          latestOption ||
          (typeof val === 'string' ? { value: val, label: val } : val)
        );
      });
      setInputValue(resolved);
    }
  }, [input.value]);

  // Re-resolve already-rendered chips against the latest `field.options`.
  // This is for conflict-status / hasError updates: when options gain or
  // lose flags, existing chips need to reflect the new metadata. Does NOT
  // read `input.value` — local state is the source of truth here.
  useEffect(() => {
    setInputValue(prev => {
      if (!field.options || prev.length === 0) return prev;
      const updated = prev.map(val => {
        const latestOption = field.options?.find(
          opt => opt.value === (typeof val === 'string' ? val : val.value)
        );
        return latestOption || val;
      });
      return JSON.stringify(updated) !== JSON.stringify(prev) ? updated : prev;
    });
  }, [field.options]);

  if (!field.options) {
    return null;
  }

  const { multiSelectInputFields } = field;

  const maxOptionsSelected = (): boolean => {
    if (multiSelectInputFields?.maxSelected) {
      return inputValue.length >= multiSelectInputFields.maxSelected;
    }
    return false;
  };

  return (
    <InputFieldWrapper className="multiSelect">
      <Autocomplete
        className={classes.root}
        placeholder={field.placeholder}
        limitTags={
          multiSelectInputFields?.maxTags
            ? multiSelectInputFields.maxTags
            : undefined
        }
        multiple
        groupBy={
          multiSelectInputFields?.group
            ? option => String(option.other)
            : undefined
        }
        id={field.id || field.name}
        options={field.options}
        getOptionLabel={option => option.label || ''}
        value={inputValue}
        filterSelectedOptions
        getOptionDisabled={maxOptionsSelected}
        getOptionSelected={(option, curr) => option.value === curr.value}
        onChange={(_, value) => {
          const newValues = value as FieldInputValue[];
          setInputValue(newValues);
          input.onChange(newValues);
          input.onBlur();
        }}
        renderTags={(value, getTagProps) => {
          return value.map((option, index) => (
            <OptionRenderer
              key={option.value}
              {...option}
              {...getTagProps({ index })}
              classes={{
                deleteIcon: classes.chipDeleteIcon
              }}
            />
          ));
        }}
        renderInput={params => (
          <Input
            field={field}
            meta={meta}
            wrapField={false}
            // CRITICAL: Do NOT spread {...input} here.
            // Autocomplete handles the search input itself via params.
            // Spreading input.onChange here causes typing to overwrite the array value in form state.
            {...params}
          />
        )}
        debug
        classes={{
          popper: classes.popper
        }}
      />
    </InputFieldWrapper>
  );
};

export default FormMultiselectField;
